import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from "recharts";
import { Activity, ChartBar as BarChartIcon, Users, ShieldCheck, Heart, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import VelocityCard from "@/components/VelocityCard";

const RATING_COLORS = ["#fda4af", "#fcd34d", "#94a3b8", "#86efac", "#34d399"];

export default function Analytics() {
  const [tower, setTower] = useState([]);
  const [dist, setDist] = useState(null);
  const [goals, setGoals] = useState(null);
  const [hc, setHc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/analytics/cycle-control-tower"),
      api.get("/analytics/rating-distribution"),
      api.get("/analytics/goal-achievement"),
      api.get("/analytics/headcount"),
    ]).then(([a, b, c, d]) => {
      setTower(a.data.items || []);
      setDist(b.data);
      setGoals(c.data);
      setHc(d.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-slate-500">Loading analytics…</div>;

  return (
    <div className="max-w-7xl space-y-6" data-testid="analytics-page">
      <div>
        <div className="axia-overline">HR · Analytics</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5">HR Analytics</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-xl">Cycle progress, rating distribution, goal achievement and headcount — across your tenant.</p>
      </div>

      <Tabs defaultValue="pulse">
        <TabsList>
          <TabsTrigger value="pulse" data-testid="tab-pulse"><Heart className="w-3.5 h-3.5 mr-1.5" /> Pulse</TabsTrigger>
          <TabsTrigger value="detailed" data-testid="tab-detailed"><BarChartIcon className="w-3.5 h-3.5 mr-1.5" /> Detailed</TabsTrigger>
        </TabsList>
        <TabsContent value="pulse" className="pt-4 space-y-6">
          <PulseHeader hc={hc} dist={dist} goals={goals} tower={tower} />
          <PulseDashboard />
        </TabsContent>
        <TabsContent value="detailed" className="pt-4 space-y-6">
          <DetailedAnalytics tower={tower} dist={dist} goals={goals} hc={hc} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PulseHeader({ hc, dist, goals, tower }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="pulse-kpi-strip">
      <KPI icon={<Users className="w-4 h-4" />} label="Active employees" value={hc?.total ?? "—"} />
      <KPI icon={<Activity className="w-4 h-4" />} label="Avg final rating" value={dist?.average ? dist.average.toFixed(2) : "—"} hint={`across ${dist?.total ?? 0} reviews`} />
      <KPI icon={<BarChartIcon className="w-4 h-4" />} label="Goal avg progress" value={`${goals?.overall?.avg_progress ?? 0}%`} hint={`${goals?.overall?.objectives ?? 0} objectives`} />
      <KPI icon={<ShieldCheck className="w-4 h-4" />} label="Active cycles" value={tower.filter((c) => c.status === "active").length} hint={`${tower.length} total tracked`} />
    </div>
  );
}

/**
 * PulseDashboard — single-glance HR pulse for Monday-morning leadership review.
 *
 * Stitches:
 *   - 3 velocity sparklines (goals approval / reviews submission / calibration commits / kudos flow)
 *   - 9-box performance × potential heatmap (committed sessions only)
 *   - Disengagement-risk proxy (quiet / stalled / unrecognised)
 *
 * Each block degrades gracefully when there is no data — empty states explain
 * what would populate them, so the page never feels broken in fresh tenants.
 */
function PulseDashboard() {
  const [goalsVel, setGoalsVel] = useState(null);
  const [reviewsVel, setReviewsVel] = useState(null);
  const [calVel, setCalVel] = useState(null);
  const [kudosVel, setKudosVel] = useState(null);
  const [nineBox, setNineBox] = useState(null);
  const [risk, setRisk] = useState(null);

  useEffect(() => {
    api.get("/objectives/org/rollup").then((r) => setGoalsVel(r.data.velocity)).catch(() => {});
    api.get("/reviews/velocity").then((r) => setReviewsVel(r.data)).catch(() => {});
    api.get("/calibration/velocity").then((r) => setCalVel(r.data)).catch(() => {});
    api.get("/analytics/kudos-velocity").then((r) => setKudosVel(r.data)).catch(() => {});
    api.get("/analytics/nine-box-distribution").then((r) => setNineBox(r.data)).catch(() => {});
    api.get("/analytics/disengagement-risk").then((r) => setRisk(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="pulse-dashboard">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {goalsVel && (
          <VelocityCard
            velocity={goalsVel}
            overline="Goal approvals · last 8 weeks"
            unit="approval" unitPlural="approvals"
            primaryColor="#10b981"
            driftLabel="Changes-requested signal"
            testId="pulse-goals-velocity"
            interpret={(v) => v.total === 0 ? "No approvals yet — cycles haven't kicked off." :
              v.delta_wow > 0 ? "Approvals accelerating week-over-week." :
              v.delta_wow < 0 ? "Approvals slowed this week." :
              "Steady approval cadence."}
          />
        )}
        {reviewsVel && (
          <VelocityCard
            velocity={reviewsVel}
            overline="Review submissions · last 8 weeks"
            unit="submission" unitPlural="submissions"
            primaryColor="#3b82f6"
            testId="pulse-reviews-velocity"
            interpret={(v) => v.total === 0 ? "No reviews submitted yet." :
              v.delta_wow > 0 ? "Reviews moving forward." :
              v.delta_wow < 0 ? "Submission pace slowed this week." :
              "Steady submission cadence."}
          />
        )}
        {calVel && (
          <VelocityCard
            velocity={calVel}
            overline="Calibration commits · last 8 weeks"
            unit="session locked" unitPlural="sessions locked"
            primaryColor="#a855f7"
            testId="pulse-calibration-velocity"
            interpret={(v) => v.total === 0 ? "No sessions committed yet." :
              v.delta_wow > 0 ? "Calibration meetings landing." :
              v.delta_wow < 0 ? "Slower commit pace this week." :
              "Steady commit cadence."}
          />
        )}
        {kudosVel && (
          <VelocityCard
            velocity={kudosVel}
            overline="Recognition flow · last 8 weeks"
            unit="kudo" unitPlural="kudos given"
            primaryColor="#f59e0b"
            testId="pulse-kudos-velocity"
            interpret={(v) => v.total === 0 ? "No kudos given yet — encourage the first one." :
              v.delta_wow > 0 ? "Recognition flowing — culture is buzzing." :
              v.delta_wow < 0 ? "Kudos volume dipped this week." :
              "Steady recognition pulse."}
          />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <NineBoxCard data={nineBox} />
        <RiskCard data={risk} />
      </div>
    </div>
  );
}

const NINE_BOX_LABELS = [
  ["Diamond", "Future leader", "Star"],
  ["Solid pro", "Core player", "High potential"],
  ["Underperformer", "Inconsistent", "Enigma"],
];

function NineBoxCard({ data }) {
  if (!data) return <div className="axia-card p-5 lg:col-span-2 text-sm text-slate-400">Loading 9-box…</div>;
  const { grid, total } = data;
  const max = Math.max(1, ...grid.flat());
  return (
    <div className="axia-card p-5 lg:col-span-2" data-testid="pulse-nine-box">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="axia-overline">9-box · committed sessions</div>
          <div className="text-sm text-slate-500 mt-1">Performance (rows) × Potential (columns) — heat reflects headcount.</div>
        </div>
        <div className="text-xs font-mono text-slate-700">{total} placed</div>
      </div>
      {total === 0 ? (
        <div className="rounded-md bg-slate-50 border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
          No committed calibration sessions yet. Run a calibration meeting in <code className="font-mono text-slate-700">/calibration</code> to populate this grid.
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2 max-w-md">
          {grid.flatMap((row, ri) => row.map((count, ci) => {
            const intensity = max > 0 ? count / max : 0;
            const bg = `rgba(16, 185, 129, ${0.05 + intensity * 0.5})`;
            return (
              <div
                key={`${ri}-${ci}`}
                className="aspect-square rounded-md border border-slate-200 p-2 flex flex-col justify-between"
                style={{ background: bg }}
                title={`${NINE_BOX_LABELS[ri][ci]}: ${count} people`}
                data-testid={`pulse-nine-box-cell-${ri}-${ci}`}
              >
                <div className="text-[9px] uppercase tracking-wider text-slate-500 leading-tight">{NINE_BOX_LABELS[ri][ci]}</div>
                <div className="font-display text-lg font-bold text-slate-900 self-end">{count}</div>
              </div>
            );
          }))}
        </div>
      )}
    </div>
  );
}

function RiskCard({ data }) {
  if (!data) return <div className="axia-card p-5 text-sm text-slate-400">Loading risk…</div>;
  const pct = data.total_employees > 0
    ? Math.round((data.overall_at_risk / data.total_employees) * 100)
    : 0;
  const tone = pct >= 30 ? "text-rose-700" : pct >= 15 ? "text-amber-700" : "text-emerald-700";
  return (
    <div className="axia-card p-5" data-testid="pulse-risk">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <div className="axia-overline">Disengagement risk</div>
      </div>
      <div className="flex items-baseline gap-2 mt-2">
        <div className={cn("font-display text-3xl font-bold tracking-tight", tone)} data-testid="pulse-risk-pct">
          {pct}%
        </div>
        <div className="text-sm text-slate-500">of {data.total_employees} employees</div>
      </div>
      <p className="text-xs text-slate-500 mt-1.5 leading-snug">
        Two or more quiet signals in the last 30–60 days. Treat as a watchlist, not a verdict.
      </p>
      <div className="mt-4 space-y-1.5 text-xs">
        <RiskRow label="Quiet — no check-ins in 30 days" value={data.quiet} total={data.total_employees} />
        <RiskRow label="Stalled — at-risk objective for 14+ days" value={data.stalled} total={data.total_employees} />
        <RiskRow label="Unrecognised — no kudos in 60 days" value={data.unrecognised} total={data.total_employees} />
      </div>
    </div>
  );
}

function RiskRow({ label, value, total }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div data-testid={`risk-row-${label.split(' ')[0].toLowerCase()}`}>
      <div className="flex justify-between text-slate-700">
        <span className="truncate pr-2">{label}</span>
        <span className="font-mono">{value} <span className="text-slate-400">· {pct}%</span></span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden">
        <div className="h-full bg-amber-400 rounded-full" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function DetailedAnalytics({ tower, dist, goals, hc }) {
  return (
    <>
      {/* Headcount strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <KPI icon={<Users className="w-4 h-4" />} label="Active employees" value={hc?.total ?? "—"} />
        <KPI icon={<Activity className="w-4 h-4" />} label="Avg final rating" value={dist?.average ? dist.average.toFixed(2) : "—"} hint={`across ${dist?.total ?? 0} reviews`} />
        <KPI icon={<BarChartIcon className="w-4 h-4" />} label="Goal avg progress" value={`${goals?.overall?.avg_progress ?? 0}%`} hint={`${goals?.overall?.objectives ?? 0} objectives`} />
        <KPI icon={<ShieldCheck className="w-4 h-4" />} label="Active cycles" value={tower.filter((c) => c.status === "active").length} hint={`${tower.length} total tracked`} />
      </div>

      {/* Cycle Control Tower */}
      <div className="axia-card p-6">
        <h2 className="font-display text-base font-semibold text-slate-900 mb-4">Cycle Control Tower</h2>
        {tower.length === 0 ? <div className="text-sm text-slate-500">No active cycles.</div> : (
          <div className="space-y-5">
            {tower.map((c) => (
              <div key={c.cycle_id} className="border border-slate-200 rounded-md p-4" data-testid={`tower-${c.cycle_id}`}>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="font-display text-sm font-semibold text-slate-900">{c.cycle_name}</div>
                    <div className="text-xs text-slate-500">{c.year} · {c.status}</div>
                  </div>
                  <div className="text-xs text-slate-600 flex items-center gap-3">
                    <span><span className="font-mono font-semibold text-slate-900">{c.participants}</span> participants</span>
                    <span><span className="font-mono font-semibold text-slate-900">{c.objectives_total}</span> objectives</span>
                    <span><span className="font-mono font-semibold text-slate-900">{c.reviews_total}</span> reviews</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  <Histogram title="Objectives by status" data={c.objectives_by_status} />
                  <Histogram title="Reviews by status" data={c.reviews_by_status} />
                  <Histogram title="Phases" data={Object.fromEntries((c.phases || []).map((p) => [p.label, p.status === "open" ? 1 : 0]))} renderLabel="status" phases={c.phases} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rating distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="axia-card p-6">
          <h2 className="font-display text-base font-semibold text-slate-900 mb-3">Rating distribution</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dist?.distribution || []}>
                <XAxis dataKey="rating" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" radius={[6, 6, 0, 0]} data-testid="rating-bar">
                  {(dist?.distribution || []).map((e, i) => <Cell key={i} fill={RATING_COLORS[i] || "#94a3b8"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="axia-card p-6">
          <h2 className="font-display text-base font-semibold text-slate-900 mb-3">Avg rating by department</h2>
          <ul className="space-y-2 text-sm" data-testid="dept-rating-list">
            {(dist?.by_department || []).slice(0, 8).map((d) => (
              <li key={d.department} className="flex items-center justify-between">
                <span className="text-slate-700">{d.department}</span>
                <span className="text-xs text-slate-500"><span className="font-mono font-semibold text-slate-900">{d.avg_rating}</span> · {d.count} reviews</span>
              </li>
            ))}
            {(dist?.by_department || []).length === 0 && <li className="text-xs text-slate-400">No data.</li>}
          </ul>
        </div>
      </div>

      {/* Goal achievement by department */}
      <div className="axia-card p-6">
        <h2 className="font-display text-base font-semibold text-slate-900 mb-3">Goal achievement by department</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left py-2">Department</th>
                <th className="text-right py-2">Objectives</th>
                <th className="text-right py-2">Avg progress</th>
                <th className="text-right py-2">On track</th>
                <th className="text-right py-2">At risk</th>
                <th className="text-right py-2">Blocked</th>
                <th className="text-right py-2">Complete</th>
              </tr>
            </thead>
            <tbody>
              {(goals?.by_department || []).map((d) => (
                <tr key={d.department} className="border-b border-slate-100" data-testid={`goal-row-${d.department}`}>
                  <td className="py-2.5 font-medium text-slate-900">{d.department}</td>
                  <td className="py-2.5 text-right font-mono">{d.objectives}</td>
                  <td className="py-2.5 text-right font-mono">{d.avg_progress}%</td>
                  <td className="py-2.5 text-right text-emerald-700">{d.on_track}</td>
                  <td className="py-2.5 text-right text-amber-700">{d.at_risk}</td>
                  <td className="py-2.5 text-right text-rose-700">{d.blocked}</td>
                  <td className="py-2.5 text-right text-slate-700">{d.complete}</td>
                </tr>
              ))}
              {(goals?.by_department || []).length === 0 && <tr><td colSpan={7} className="py-6 text-center text-xs text-slate-500">No data.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Headcount */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="axia-card p-6">
          <h2 className="font-display text-base font-semibold text-slate-900 mb-3">Headcount by department</h2>
          <ul className="space-y-1.5 text-sm">
            {(hc?.by_department || []).slice(0, 12).map((d) => (
              <li key={d.department} className="flex items-center justify-between">
                <span className="text-slate-700">{d.department}</span>
                <span className="font-mono text-xs text-slate-500">{d.count}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="axia-card p-6">
          <h2 className="font-display text-base font-semibold text-slate-900 mb-3">Employment status</h2>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={hc?.by_status || []} dataKey="count" nameKey="status" innerRadius={50} outerRadius={80}>
                  {(hc?.by_status || []).map((e, i) => <Cell key={i} fill={["#0F172A", "#94a3b8", "#fbbf24", "#f87171", "#34d399"][i % 5]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="mt-2 space-y-1 text-xs">
            {(hc?.by_status || []).map((s) => (
              <li key={s.status} className="flex items-center justify-between text-slate-600">
                <span className="capitalize">{(s.status || "").replace("_", " ")}</span>
                <span className="font-mono">{s.count}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}

function KPI({ icon, label, value, hint }) {
  return (
    <div className="axia-card p-4">
      <div className="flex items-center gap-2 text-slate-500">{icon}<div className="axia-overline">{label}</div></div>
      <div className="font-display text-2xl font-bold text-slate-900 mt-1">{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function Histogram({ title, data, renderLabel, phases }) {
  const entries = Object.entries(data || {});
  return (
    <div>
      <div className="axia-overline mb-1.5">{title}</div>
      {entries.length === 0 ? <div className="text-xs text-slate-400">—</div> : (
        <ul className="space-y-1">
          {entries.map(([k, v]) => {
            let display = v;
            if (renderLabel === "status" && phases) {
              const phase = phases.find((p) => p.label === k);
              display = phase?.status || "?";
            }
            return (
              <li key={k} className="flex items-center justify-between text-xs">
                <span className="text-slate-600 capitalize">{(k || "").replace(/_/g, " ")}</span>
                <span className="font-mono text-slate-900">{display}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
